<template>
  <div class="table-view">
    <el-table
      :data="props.tableData"
      v-loading="props.loading"
      :element-loading-text="props.loadingTxt"
    >
      <template #empty>
        <slot name="empty"></slot>
      </template>

      <!-- 表头 -->
      <template v-for="column in props.columns" :key="column.prop">
        <el-table-column
          :prop="column.prop"
          :label="column.label"
          :align="column.align || 'center'"
          :min-width="80"
          :sortable="column.sortable || false"
          :fixed="column.fixed || false"
          :class-name="cellClassName(column)"
          :width="column.width"
        >
          <!-- 自定义插槽头部 -->
          <template v-if="column.slotHead" #header>
            <slot :name="column.slotHead"></slot>
          </template>
          <!-- 自定义插槽内容 -->
          <template v-if="column.slot" #default="scope">
            <slot :name="column.slot" :scope="scope" />
          </template>
          <template v-if="column.tooltip" #default="scope">
            <el-tooltip
              effect="dark"
              :content="getToolTipTxt(column, scope.row)"
              :enterable="column.enterable ? column.enterable : false"
              placement="top-start"
              transition=""
              :offset="0"
            >
              {{ getToolTipTxt(column, scope.row) }}
            </el-tooltip>
          </template>
        </el-table-column>
      </template>

      <!-- 操作列 -->
      <el-table-column
        v-if="props.actionColumn"
        class-name="operate-col"
        align="center"
        label="操作"
      >
        <template #default="scope">
          <slot name="actions" :scope="scope" />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup>
const props = defineProps({
  tableData: {
    type: Array
  }, // 数据
  columns: {
    type: Array
  } /*
    表头数据.
    tooltip：true（单元格开启溢出隐藏，且使用 tooltip 显示完整内容）;
    enterable: true（鼠标可以移入 tooltip 上，默认 false 不可以）
   */,
  actionColumn: {
    type: Boolean
  }, // 是否需要"操作列"
  loading: {
    type: Boolean
  }, // 是否显示加载中遮罩?
  loadingTxt: {
    type: String
  } // 加载时的文案
})

const cellClassName = column => {
  return [column.className, column.tooltip ? 'overflow-tooltip' : ''].filter(Boolean).join(' ')
}

const getToolTipTxt = (col, scope) => {
  return scope[col.prop]
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_make.scss';
@import '@/assets/scss/common.scss';

.table-view {
  :deep(.el-table) {
    height: 100%;
    color: #585a5c;

    @include responsive-style(
      (
        font-size: 14px
      )
    );

    .el-table__header {
      thead {
        color: #0f0f0f;
        font-weight: 400;
      }

      th {
        font-weight: 400;
        @include responsive-style(
          (
            padding: 12px 0
          )
        );

        &:first-of-type {
          @include responsive-style(
            (
              border-radius: 10px 0 0 0
            )
          );
        }

        &:last-of-type {
          @include responsive-style(
            (
              border-radius: 0 10px 0 0
            )
          );
        }
      }

      .el-table__cell {
        border: none;
        background-color: #f5f5f5;
      }

      .cell {
        @include responsive-style(
          (
            font-size: 14px,
            line-height: 16px
          )
        );
      }
    }

    .el-table__body-wrapper {
      @include responsive-style(
        (
          box-shadow: 0 0 0 1px #f5f5f5 inset,
          border-radius: 0 0 10px 10px
        )
      );
    }

    .el-table__body {
      tr:hover > td.el-table__cell {
        background-color: transparent;
      }

      .el-table__cell {
        border: none;
        @include responsive-style(
          (
            padding: 14px 0
          )
        );
      }

      .operate-col {
        padding: 0;
      }

      .overflow-tooltip {
        padding: 0;
        .cell {
          text-wrap: nowrap;

          .el-tooltip__trigger {
            @include responsive-style(
              (
                padding: 14px 0
              )
            );
          }
        }
      }
    }

    tr {
      background-color: transparent;
    }

    .cell {
      line-height: 1.5;
    }

    .el-table__inner-wrapper::before {
      display: none;
    }
  }

  :deep(.el-loading-spinner) {
    .path {
      stroke: $--themecolor;
    }
    .el-loading-text {
      color: $--themecolor;
    }
  }
}
</style>
